package ru.chm.gradereport.ui.screens.login

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.viewinterop.HtmlElementView
import kotlinx.browser.document
import org.w3c.dom.Element
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event

private const val FORM_STYLES_ID = "chm-login-form-styles"

private const val INPUT_CSS = """
.chm-login-input {
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  padding: 0 14px;
  border: 1px solid var(--chm-border);
  border-radius: 10px;
  background: var(--chm-bg);
  color: var(--chm-text);
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  outline: none;
  transition: border-color 120ms ease;
}
.chm-login-input:focus { border-color: var(--chm-accent); }
.chm-login-input::placeholder { color: var(--chm-muted); opacity: 1; }
.chm-login-input + .chm-login-input { margin-top: 10px; }
.chm-login-submit {
  width: 100%;
  height: 44px;
  margin-top: 16px;
  border: none;
  border-radius: 10px;
  background: var(--chm-accent);
  color: #ffffff;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.chm-login-submit:hover:not(:disabled) { filter: brightness(1.07); }
.chm-login-submit:disabled { opacity: 0.6; cursor: default; }
"""

/** Injects the static form CSS once (pseudo-classes can't be done with inline styles). */
private fun ensureFormStyles() {
    if (document.getElementById(FORM_STYLES_ID) != null) return
    val styleElement = document.createElement("style")
    styleElement.id = FORM_STYLES_ID
    styleElement.textContent = INPUT_CSS.trimIndent()
    (document.getElementsByTagName("head").item(0) as? HTMLElement)?.appendChild(styleElement)
}

@OptIn(ExperimentalComposeUiApi::class)
@Composable
internal actual fun NativeLoginForm(
    style: NativeLoginFormStyle,
    loading: Boolean,
    submitText: String,
    onSubmit: (login: String, password: String) -> Unit,
    modifier: Modifier,
) {
    val currentOnSubmit by rememberUpdatedState(onSubmit)

    HtmlElementView(
        factory = {
            ensureFormStyles()

            val form = document.createElement("form") as HTMLFormElement

            val login = document.createElement("input") as HTMLInputElement
            login.type = "text"
            login.name = "username"
            login.required = true
            login.placeholder = "Логин"
            login.className = "chm-login-input"
            login.setAttribute("autocomplete", "username")
            login.setAttribute("autocapitalize", "none")
            login.setAttribute("spellcheck", "false")

            val password = document.createElement("input") as HTMLInputElement
            password.type = "password"
            password.name = "password"
            password.required = true
            password.placeholder = "Пароль"
            password.className = "chm-login-input"
            password.setAttribute("autocomplete", "current-password")

            val button = document.createElement("button") as HTMLButtonElement
            button.type = "submit"
            button.className = "chm-login-submit"

            form.appendChild(login)
            form.appendChild(password)
            form.appendChild(button)

            // Native submit (click or Enter) is what triggers the browser's
            // "save password?" prompt; we only replace the default navigation.
            form.addEventListener(
                "submit",
                { event: Event ->
                    event.preventDefault()
                    val user = login.value.trim()
                    val pass = password.value
                    if (user.isNotEmpty() && pass.isNotEmpty()) {
                        currentOnSubmit(user, pass)
                    }
                },
            )

            form
        },
        modifier = modifier,
        update = { form: HTMLFormElement ->
            // Colors ride on CSS variables so the :focus/::placeholder rules
            // inside the injected stylesheet stay theme-aware.
            form.style.setProperty("--chm-bg", style.background)
            form.style.setProperty("--chm-border", style.border)
            form.style.setProperty("--chm-text", style.text)
            form.style.setProperty("--chm-muted", style.muted)
            form.style.setProperty("--chm-accent", style.accent)

            val button = form.elements.item(2) as HTMLButtonElement
            button.disabled = loading
            button.textContent = submitText
        },
    )
}
